import { Device, RawDevice } from '../types';

export interface DevicesState {
  devices: Device[];
  devicesLoading: boolean;
  devicesError: string | null;
  devicesLastFetchedAt?: number | null;
  devicesLastHardRefreshAt?: number | null;
}

export interface DevicesActions {
  fetchDevices: (opts?: { force?: boolean; hard?: boolean }) => Promise<void>;
  hardRefreshDevices: () => Promise<void>;
  testDevice: (deviceId: string) => Promise<void>;
}

let inFlightDevicesPromise: Promise<void> | null = null;
const DEVICES_TTL_MS = 30_000; // 30s client-side TTL to align with server
// Hard-refresh cadence: bypass server-side 30s device cache periodically
// so newly plugged/unplugged devices appear without requiring a manual refresh.
const HARD_REFRESH_TTL_MS = 60_000;

export const createDevicesModule = (set: any, get: any) => ({
  devices: [],
  devicesLoading: false,
  devicesError: null,
  devicesLastFetchedAt: null,
  devicesLastHardRefreshAt: null,

  fetchDevices: async (opts?: { force?: boolean; hard?: boolean }) => {
    const { sdk } = get();
    if (!sdk) return;
    const force = !!opts?.force;

    // Short-circuit if cache is fresh and not forced
    const now = Date.now();
    const last = get().devicesLastFetchedAt as number | null;
    if (!force && last && now - last < DEVICES_TTL_MS && (get().devices || []).length > 0) {
      return;
    }

    // Periodically bust the server's 30s device cache so newly attached devices
    // are not hidden behind stale state.
    const lastHard = get().devicesLastHardRefreshAt as number | null;
    const shouldHardRefresh = !!opts?.hard || !lastHard || now - lastHard > HARD_REFRESH_TTL_MS;

    // Coalesce concurrent callers
    if (inFlightDevicesPromise) {
      try { await inFlightDevicesPromise; } catch { /* ignore; next section will handle state */ }
      return;
    }

    set({ devicesLoading: true, devicesError: null });

    inFlightDevicesPromise = (async () => {
      try {
        let devices: RawDevice[];
        if (shouldHardRefresh) {
          const refreshResp = await sdk.devices.refresh();
          devices = (refreshResp && refreshResp.devices) || await sdk.devices.listAll();
          set({ devicesLastHardRefreshAt: Date.now() });
        } else {
          devices = await sdk.devices.listAll();
        }

        const processedDevices = devices.map((device: RawDevice, index: number) => {

          const id = device.HexID || `device-${index}`;
          const name = device.Name || `Device ${index + 1}`;
          const type = device.Type || 'unknown';

          return {
            id,
            name,
            type,
            status: 'active' as const,
          };
        });

        set({
          devices: processedDevices,
          devicesLoading: false,
          devicesError: null,
          devicesLastFetchedAt: Date.now(),
        });
      } catch (error) {
        set({
          devicesLoading: false,
          devicesError: error instanceof Error ? error.message : 'Failed to fetch devices',
        });
      } finally {
        inFlightDevicesPromise = null;
      }
    })();

    await inFlightDevicesPromise;
  },

  hardRefreshDevices: async () => {
    await (get() as any).fetchDevices({ force: true, hard: true });
  },

  testDevice: async (deviceId: string) => {
    const { sdk } = get();
    if (!sdk) return;
    
    try {
      await sdk.devices.testDevice(deviceId);
      set((state: any) => ({
        devices: state.devices.map((device: Device) =>
          device.id === deviceId ? { ...device, status: 'active' as const } : device
        ),
      }));
    } catch (error) {
      set((state: any) => ({
        devices: state.devices.map((device: Device) =>
          device.id === deviceId ? { ...device, status: 'error' as const } : device
        ),
      }));
    }
  },
});
